import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (e, href) => {
    // For links with a hash that target a different page (e.g. "/#faq" from "/car-loans")
    const [path, hash] = href.split('#');
    const targetPath = path || '/';

    if (hash) {
      e.preventDefault();
      if (location.pathname === targetPath) {
        // Same page — just scroll to anchor
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Different page — navigate, ScrollToHash will handle the scroll
        navigate(`${targetPath}#${hash}`);
      }
    }
    // Non-hash links (e.g. "/contact", "/process") — let the default <a> behaviour work
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = brand.navLinks;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md py-3 transition-all duration-300"
      data-testid="main-navigation"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href={brand.id === 'costco' ? '/costco' : '/'} className="flex items-center space-x-3" data-testid="nav-logo">
            {brand.id === 'costco' && (
              <>
                <img
                  src="/images/costco-logo.png"
                  alt="Costco Wholesale"
                  className="h-8 w-auto"
                />
                <span className="text-gray-300 text-lg font-light">|</span>
              </>
            )}
            <img
              src="/images/myl-logo-landscape.png"
              alt="Manage Your Loans"
              className="h-8 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="font-medium text-[#00594C] transition-colors hover:text-[#99DF63]"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+61468007056"
              className="flex items-center space-x-2 px-4 py-2 rounded-full text-[#00594C] hover:bg-gray-100 transition-all"
              data-testid="nav-phone-button"
            >
              <Phone className="w-4 h-4" />
              <span>0468 007 056</span>
            </a>
            <a
              href="#book-appointment"
              onClick={(e) => handleNavClick(e, '#book-appointment')}
              className="flex items-center space-x-2 bg-[#99DF63] text-[#00594C] px-6 py-2 rounded-full font-semibold hover:bg-[#8BCF52] transition-all"
              data-testid="nav-book-button"
            >
              <Calendar className="w-4 h-4" />
              <span>Book Appointment</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className="text-[#00594C]" />
            ) : (
              <Menu className="text-[#00594C]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4 p-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-[#00594C] font-medium hover:text-[#99DF63] transition-colors"
                  onClick={(e) => { handleNavClick(e, link.href); setIsMobileMenuOpen(false); }}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="tel:+61468007056"
                className="flex items-center space-x-2 text-[#00594C] font-medium"
                data-testid="mobile-phone-button"
              >
                <Phone className="w-4 h-4" />
                <span>0468 007 056</span>
              </a>
              <a
                href="#book-appointment"
                onClick={(e) => { handleNavClick(e, '#book-appointment'); setIsMobileMenuOpen(false); }}
                className="flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-6 py-3 rounded-full font-semibold"
                data-testid="mobile-book-button"
              >
                <Calendar className="w-4 h-4" />
                <span>Book Appointment</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;